import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Seo from '../components/Seo';

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(6);

  // Fetching the data from your "Vault" API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await axios.get('https://toporion.net/vault');
        if (res.data.success) {
          setBlogPosts(res.data.data);
          setFilteredPosts(res.data.data);
        }
      } catch (error) {
        console.error("FAILED_TO_LOAD_VAULT:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // Search/filter functionality
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPosts(blogPosts);
    } else {
      const filtered = blogPosts.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.keywords && Array.isArray(post.keywords) && post.keywords.some(k => k.toLowerCase().includes(searchTerm.toLowerCase())))
      );
      setFilteredPosts(filtered);
    }
    setCurrentPage(1); // Reset to first page when searching
  }, [searchTerm, blogPosts]);

  // Pagination logic
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Helper to format the MongoDB date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options).toUpperCase();
  };

  // Get unique categories/keywords for sidebar
  const getAllCategories = () => {
    const categories = new Set();
    blogPosts.forEach(post => {
      if (post.keywords && Array.isArray(post.keywords)) {
        post.keywords.forEach(k => categories.add(k));
      } else if (post.keywords && typeof post.keywords === 'string') {
        post.keywords.split(',').forEach(k => categories.add(k.trim()));
      }
    });
    return Array.from(categories).slice(0, 8);
  };

  // Generate page numbers array with ellipsis
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0f172a] flex items-center justify-center">
      <p className="text-[#00F0FF] font-mono animate-pulse uppercase tracking-widest">Scanning Records...</p>
    </div>
  );

  return (
    <>
      <Seo 
        title="Engineering Log | Technical Insights from Top Orion"
        description="Explore technical articles, case studies, and engineering protocols about ERP systems, restaurant inventory management, fitness trainer solutions, and web development best practices."
        keywords="ERP systems, restaurant inventory management, fitness trainer software, web development, technical blog, engineering log, SaaS solutions, inventory leakage prevention"
        type="website"
      />

      <div className="min-h-screen bg-[#0f172a] text-white pt-24 pb-24 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="mb-16 border-b border-white/10 pb-8">
            <span className="text-[#00F0FF] font-mono text-sm tracking-widest uppercase mb-2 block">// Knowledge Base</span>
            <h1 className="text-5xl md:text-7xl font-black font-display text-white">
              ENGINEERING <span className="text-gray-600">LOG</span>
            </h1>
            <p className="text-gray-500 mt-4 max-w-2xl font-mono text-sm">
              Technical protocols, case studies, and implementation guides from the Top Orion engineering team.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* === LEFT COLUMN: DYNAMIC CONTENT FEED === */}
            <div className="lg:col-span-8 space-y-12">
              {currentPosts.length > 0 ? (
                <>
                  {currentPosts.map((post) => (
                    <article key={post._id} className="group bg-[#18181b] border border-white/5 rounded-3xl overflow-hidden hover:border-[#00F0FF]/30 transition-all duration-500 hover:-translate-y-1">
                        <div className="h-[300px] overflow-hidden relative">
                            <img 
                                src={post.featuredImage} 
                                alt={post.title} 
                                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 filter grayscale-[50%] group-hover:grayscale-0" 
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#18181b] to-transparent opacity-80"></div>
                            
                            <div className="absolute top-6 left-6">
                                <span className="bg-[#00F0FF]/10 text-[#00F0FF] border border-[#00F0FF]/20 px-3 py-1 rounded text-xs font-mono font-bold uppercase tracking-widest backdrop-blur-md">
                                    {post.keywords && (Array.isArray(post.keywords) ? post.keywords[0] : post.keywords.split(',')[0]) || "SYSTEM"}
                                </span>
                            </div>
                        </div>

                        <div className="p-8 md:p-10 relative">
                            <div className="flex items-center gap-3 text-xs font-mono text-gray-500 mb-4">
                                <span>// {formatDate(post.createdAt)}</span>
                                <span className="w-1 h-1 bg-gray-600 rounded-full"></span>
                                <span>{post.author?.toUpperCase() || "SALMAN HAIDER"}</span>
                            </div>

                            <h2 className="text-3xl font-bold font-display text-white mb-4 group-hover:text-[#00F0FF] transition-colors">
                                <Link to={`/blog/${post.slug}`}>
                                    {post.title}
                                </Link>
                            </h2>

                            <p className="text-gray-400 text-sm leading-relaxed mb-8 max-w-2xl">
                                {post.description}
                            </p>

                            <Link 
                                to={`/blog/${post.slug}`}
                                className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-widest border-b border-[#00F0FF] pb-1 hover:text-[#00F0FF] transition-colors"
                            >
                                Read Protocol
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3" />
                                </svg>
                            </Link>
                        </div>
                    </article>
                  ))}

                  {/* === BEAUTIFUL PAGINATION === */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 pt-8 border-t border-white/10">
                      {/* Previous Button */}
                      <button
                        onClick={prevPage}
                        disabled={currentPage === 1}
                        className={`px-4 py-2 rounded-xl font-mono text-sm transition-all duration-300 flex items-center gap-2 ${
                          currentPage === 1 
                            ? 'bg-white/5 text-gray-600 cursor-not-allowed' 
                            : 'bg-[#18181b] border border-white/10 text-white hover:border-[#00F0FF] hover:text-[#00F0FF] hover:-translate-y-0.5'
                        }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Previous
                      </button>

                      {/* Page Numbers */}
                      <div className="flex gap-2">
                        {getPageNumbers().map((page, index) => (
                          page === '...' ? (
                            <span key={index} className="px-3 py-2 text-gray-500 font-mono text-sm">...</span>
                          ) : (
                            <button
                              key={index}
                              onClick={() => paginate(page)}
                              className={`px-4 py-2 rounded-xl font-mono text-sm font-bold transition-all duration-300 ${
                                currentPage === page
                                  ? 'bg-[#00F0FF] text-black shadow-lg shadow-[#00F0FF]/20'
                                  : 'bg-[#18181b] border border-white/10 text-white hover:border-[#00F0FF] hover:text-[#00F0FF] hover:-translate-y-0.5'
                              }`}
                            >
                              {page}
                            </button>
                          )
                        ))}
                      </div>

                      {/* Next Button */}
                      <button
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                        className={`px-4 py-2 rounded-xl font-mono text-sm transition-all duration-300 flex items-center gap-2 ${
                          currentPage === totalPages 
                            ? 'bg-white/5 text-gray-600 cursor-not-allowed' 
                            : 'bg-[#18181b] border border-white/10 text-white hover:border-[#00F0FF] hover:text-[#00F0FF] hover:-translate-y-0.5'
                        }`}
                      >
                        Next
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Results counter */}
                  <div className="text-center text-gray-500 font-mono text-xs">
                    Showing {indexOfFirstPost + 1} - {Math.min(indexOfLastPost, filteredPosts.length)} of {filteredPosts.length} protocols
                  </div>
                </>
              ) : (
                <div className="p-20 border border-dashed border-white/10 text-center rounded-3xl">
                  <p className="text-gray-600 font-mono italic">No protocols found in the archive.</p>
                </div>
              )}
            </div>

            {/* === RIGHT COLUMN: DYNAMIC SIDEBAR === */}
            <div className="lg:col-span-4 space-y-12">
              {/* Search Database */}
              <div className="bg-[#18181b] border border-white/5 p-8 rounded-2xl sticky top-24">
                <h3 className="text-white font-bold font-display uppercase tracking-widest mb-6 text-sm">Search Database</h3>
                <div className="relative">
                  <input 
                    type="text" 
                    placeholder="Keywords..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-black/40 border border-white/10 rounded-lg py-3 px-4 text-white text-sm focus:outline-none focus:border-[#00F0FF] transition-colors font-mono placeholder-gray-700" 
                  />
                  {searchTerm && (
                    <button 
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>
                {searchTerm && (
                  <p className="text-xs text-gray-500 mt-3 font-mono">
                    Found {filteredPosts.length} result{filteredPosts.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>

              {/* Categories/Tags */}
              <div className="bg-[#18181b] border border-white/5 p-8 rounded-2xl">
                <h3 className="text-white font-bold font-display uppercase tracking-widest mb-6 text-sm">Categories</h3>
                <div className="flex flex-wrap gap-2">
                  {getAllCategories().map((category, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSearchTerm(category)}
                      className="px-3 py-1.5 bg-black/40 border border-white/10 rounded-lg text-xs font-mono text-gray-400 hover:text-[#00F0FF] hover:border-[#00F0FF] transition-all"
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Recent Signals */}
              <div className="bg-[#18181b] border border-white/5 p-8 rounded-2xl">
                <h3 className="text-white font-bold font-display uppercase tracking-widest mb-6 text-sm">Recent Signals</h3>
                <div className="space-y-6">
                  {blogPosts.slice(0, 5).map((post, idx) => (
                    <Link key={post._id} to={`/blog/${post.slug}`} className="group block cursor-pointer">
                      <span className="block text-[#00F0FF] text-[10px] font-mono mb-1 opacity-50">LOG ENTRY 0{idx+1}</span>
                      <h4 className="text-gray-300 text-sm font-bold group-hover:text-white transition-colors leading-snug">{post.title}</h4>
                      <p className="text-gray-600 text-[10px] font-mono mt-1">{formatDate(post.createdAt)}</p>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Newsletter/CTA Section */}
              <div className="bg-gradient-to-br from-[#00F0FF]/10 to-transparent border border-[#00F0FF]/20 p-8 rounded-2xl">
                <h3 className="text-white font-bold font-display uppercase tracking-widest mb-3 text-sm">Subscribe</h3>
                <p className="text-gray-400 text-xs mb-4">Get the latest engineering insights delivered to your inbox.</p>
                <input 
                  type="email" 
                  placeholder="your@email.com" 
                  className="w-full bg-black/40 border border-white/10 rounded-lg py-2 px-3 text-white text-xs mb-2"
                />
                <button className="w-full bg-[#00F0FF] text-black font-bold py-2 rounded-lg text-xs uppercase tracking-widest hover:bg-[#00F0FF]/80 transition-colors">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;